import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { moduleIdFromUrl } from '../modules/module-url';
import { isUnderBase, trimTrailingSlash } from './base-url';

/**
 * The module backends this installation knows — `environment.api.modules`,
 * read once and normalised. The shell has no backend of its own: every address
 * here belongs to one module, and the interceptors decide by these addresses
 * which requests get a prefix, a token and the company header.
 */
const MODULE_BASES: ReadonlyMap<string, { base: string; companyHeader: boolean }> = new Map(
  Object.entries(environment.api.modules).map(([id, api]) => [
    id,
    { base: trimTrailingSlash(api.baseUrl), companyHeader: api.companyHeader === true },
  ]),
);

/** Base address of one module's backend (no trailing slash), or `undefined` when it has none. */
export function moduleApiBase(moduleId: string): string | undefined {
  return MODULE_BASES.get(moduleId)?.base;
}

/** Every module backend address — the bearer-token allowlist, module part. */
export function allModuleApiBases(): readonly string[] {
  return [...MODULE_BASES.values()].map((entry) => entry.base);
}

/** Whether `url` addresses some module's backend. */
export function isModuleApiUrl(url: string): boolean {
  return [...MODULE_BASES.values()].some((entry) => isUnderBase(url, entry.base));
}

/** Whether `url` addresses a module backend that reads `X-Company-Id`. */
export function wantsCompanyHeader(url: string): boolean {
  return [...MODULE_BASES.values()].some(
    (entry) => entry.companyHeader && isUnderBase(url, entry.base),
  );
}

/**
 * The backend of the module the user is inside — taken from the address the
 * router is at or navigating to, so a request fired from a guard or resolver
 * of the module's own routes already resolves against the right service.
 * `undefined` on the shell's own screens and for modules without a backend.
 *
 * Injection context required (reads the `Router`).
 */
export function activeModuleApiBase(): string | undefined {
  const router = inject(Router);
  const target = router.getCurrentNavigation()?.finalUrl;
  const url = target ? router.serializeUrl(target) : router.url;
  const moduleId = moduleIdFromUrl(url);

  return moduleId === undefined ? undefined : moduleApiBase(moduleId);
}
